import React from 'react';
import { Link } from 'react-router';
import { STATS_BY_STATUS, STATS_BY_TYPE, CLOSING_STATS, V7_PROPERTIES_LIST_LITE } from '../../consts/queries';
import filter from '../../filter';
import { preprocessQueryFilters } from '../../utils';
import InfoColumns from './InfoColumns';
import { StatsData } from './models/stats';
import { executeQuery } from "../../requests/executeQuery";

import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import highcharts3D from 'highcharts/highcharts-3d.src';
import { summitLogo } from '../../consts/consts';

highcharts3D(Highcharts);

const INVESTMENT_SALES = 'Investment Sales';

export default class Stats extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            stats: new StatsData({}),
            totalClosedSum: null,
            totalClosedCount: null,
            isYtdClosing: true,
            loading: false,
            buyerStateSlices: [],
            hiddenBuyerStates: {},
        };

        this.buyerStateChart = React.createRef();
        this.handleChange = this.handleChange.bind(this);
    }

    handleChange(e) {
        this.setState({ source: e.currentTarget.value });
    }

    fetch() {
        const ytd = new Date().getFullYear();
        const sinceDate = `${ytd - 2}-01-01`; // since 2 years ago

        this.setState({ loading: true });

        const now = new Date();
        const eighteenMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 18, 1);
        const buyerStateMinDate = (eighteenMonthsAgo.getMonth() + 1) + '/01/' + eighteenMonthsAgo.getFullYear();
        // Exactly the rows behind /list?SUMMIT_CLOSINGS=true for the same window,
        // built through the same expansion the list page uses: asking the server
        // for Summit closings rather than for every closing and sieving them here
        // is what keeps the two counts equal, Extra Payment rows and portfolio
        // roll-ups included. Referrals are left out: both charts fed from this
        // list count Investment Sales only.
        const closedSinceOptions = filter(preprocessQueryFilters({
            SUMMIT_CLOSINGS: 'true',
            CLOSE_OF_ESCROW_MIN: buyerStateMinDate,
            TRANSACTION_TYPE: INVESTMENT_SALES,
        }));
        closedSinceOptions.param.limit = 9999;
        closedSinceOptions.param.offset = 0;

        const closedListPromise = executeQuery({ queryName: V7_PROPERTIES_LIST_LITE, options: closedSinceOptions, expireTime: 1000 })
            .catch(() => ({ success: false, data: [] }));

        Promise.all([
            executeQuery({ queryName: STATS_BY_STATUS, params: { SINCE: sinceDate } }),
            executeQuery({ queryName: STATS_BY_TYPE, params: {} }),
            executeQuery({ queryName: CLOSING_STATS }),
            closedListPromise,
        ]).then((res) => {
            const statusOk = res && res[0] && res[0].success && Array.isArray(res[0].data);
            const typeOk = res && res[1] && res[1].success && Array.isArray(res[1].data);
            const closingOk = res && res[2] && res[2].success && Array.isArray(res[2].data);
            const closedListOk = res && res[3] && res[3].success && Array.isArray(res[3].data);

            if (statusOk && typeOk) {
                const stats = new StatsData({
                    dataByStatus: res[0].data[0],
                    dataByType: res[1].data[0],
                });

                const isYtdClosing = !(res[0].data[0] && res[0].data[0].CLOSED_YTD_PROPERTIES && parseInt(res[0].data[0].CLOSED_YTD_PROPERTIES) < 7);

                let sum = res[1].data[0].CLOSED_PRICE;
                let count = res[1].data[0].CLOSED_COUNT;
                sum = (sum / Math.pow(10, 9)).toString();
                sum = sum.slice(0, sum.indexOf('.') + 2);
                count = parseInt(count / 25) * 25;

                const hardcodedData = [
                    { CLOSE_OF_ESCROW: '2020', CLOSED_PRICE: 283000000, CLOSED_PROPERTIES: 76 },
                    { CLOSE_OF_ESCROW: '2021', CLOSED_PRICE: 416000000, CLOSED_PROPERTIES: 107 },
                    { CLOSE_OF_ESCROW: '2022', CLOSED_PRICE: 480000000, CLOSED_PROPERTIES: 102 },
                    { CLOSE_OF_ESCROW: '2023', CLOSED_PRICE: 276000000, CLOSED_PROPERTIES: 74 },
                    { CLOSE_OF_ESCROW: '2024', CLOSED_PRICE: 444000000, CLOSED_PROPERTIES: 122 },
                    { CLOSE_OF_ESCROW: '2025', CLOSED_PRICE: 584000000, CLOSED_PROPERTIES: 130 },
                ];

                const ytdStart = new Date(ytd, 0, 1);

                // One lite query (18mo of Summit closings); YTD bar uses CLOSE_OF_ESCROW vs calendar-year start
                if (closedListOk) {
                    const summitYtdItems = res[3].data.filter((item) => {
                        const d = item.CLOSE_OF_ESCROW ? new Date(item.CLOSE_OF_ESCROW) : null;
                        return d && !isNaN(d.getTime()) && d >= ytdStart;
                    });
                    const ytdPrice = summitYtdItems.reduce((total, item) => total + (Number(item.SOLD_PRICE) || 0), 0);
                    const ytdCount = summitYtdItems.length;
                    hardcodedData.push({
                        CLOSE_OF_ESCROW: String(ytd),
                        CLOSED_PRICE: ytdPrice,
                        CLOSED_PROPERTIES: ytdCount,
                    });
                } else {
                    hardcodedData.push({ CLOSE_OF_ESCROW: String(ytd), CLOSED_PRICE: 0, CLOSED_PROPERTIES: 0 });
                }

                const dynamicData = closingOk ? res[2].data.filter(data => Number(data.CLOSE_OF_ESCROW) > ytd) : [];

                const convertedBarData = [...hardcodedData, ...dynamicData];

                if (Object.keys(convertedBarData).length) {
                    convertedBarData.sort((a, b) => Number(a.CLOSE_OF_ESCROW) - Number(b.CLOSE_OF_ESCROW));
                }
                let barChartData = Stats.buildChartOptions(convertedBarData);
                const inventoryCount = parseInt(stats.activeSummitCount) || 0;
                const underContractCount = parseInt(stats.negUcCount) || 0;
                const pieChartData = Stats.buildPieChartOptions(inventoryCount, underContractCount);
                const buyerStateSlices = closedListOk ? Stats.buildBuyerStateSlices(res[3].data, buyerStateMinDate) : [];
                const closingsPieData = Stats.buildBuyerStatePieOptions(buyerStateSlices);
                this.setState({
                    stats,
                    totalClosedSum: sum,
                    totalClosedCount: count,
                    isYtdClosing,
                    barChartData,
                    pieChartData,
                    closingsPieData,
                    buyerStateSlices,
                    hiddenBuyerStates: {},
                });
            }
        }).finally(() => {
            this.setState({ loading: false });
        });
    }

    componentDidMount() {
        this.fetch();
    }

    static buildChartOptions(currentAgentData) {
        if (currentAgentData) {
            const maxDataValue = Math.max(...currentAgentData.map(data => Number(data.CLOSED_PRICE)));
            const maxYAxis = Math.ceil(maxDataValue / 50000000) * 50000000;

            const tickInterval = 50000000;
            const tickPositions = [];
            for (let i = 0; i <= maxYAxis; i += tickInterval) {
                tickPositions.push(i);
            }

            const options = {
                chart: {
                    type: "column",
                    backgroundColor: '#ffffff',
                },
                title: { text: '' },
                xAxis: {
                    categories: currentAgentData.map(data => data.CLOSE_OF_ESCROW),
                    lineColor: '#e0e0e0',
                    tickColor: '#e0e0e0',
                    labels: {
                        style: { color: '#6B6B68', fontSize: '13px' }
                    }
                },
                yAxis: {
                    title: { text: '' },
                    max: maxYAxis,
                    tickPositions: tickPositions,
                    gridLineColor: '#f0f0f0',
                    labels: {
                        formatter: function () {
                            return '$' + (this.value / 1000000) + 'M';
                        },
                        style: { color: '#6B6B68', fontSize: '12px' }
                    }
                },
                tooltip: {
                    formatter: function () {
                        let value = this.y;
                        let formatted;
                        if (value >= 1000000) {
                            formatted = '$' + Math.round(value / 1000000) + 'M';
                        } else if (value >= 1000) {
                            formatted = '$' + Math.round(value / 1000) + 'K';
                        } else {
                            formatted = '$' + value;
                        }
                        return '<b>' + this.x + '</b><br/>Value: ' + formatted + '<br/>Properties: ' + this.point.closed;
                    },
                    backgroundColor: '#ffffff',
                    borderColor: '#e0e0e0',
                    borderRadius: 4,
                    style: { fontSize: '13px' }
                },
                plotOptions: {
                    series: {
                        borderWidth: 0,
                        borderRadius: 3,
                        cursor: 'pointer',
                        events: {
                            click: function (event) {
                                const year = event.point.category;
                                if (String(year) === String(new Date().getFullYear())) {
                                    window.open('/list?SUMMIT_YTD=true&TRANSACTION_TYPE=Investment%20Sales&order=CLOSE_OF_ESCROW.desc', 'blank');
                                } else {
                                    window.open(`/list?SOURCE=SUMMIT&STATUS=Closed&CLOSE_OF_ESCROW_MIN=01%2F01%2F${year}&CLOSE_OF_ESCROW_MAX=12%2F31%2F${year}`, 'blank');
                                }
                            }
                        },
                        dataLabels: {
                            enabled: true,
                            align: 'center',
                            crop: false,
                            overflow: 'none',
                            formatter: function () {
                                let value = this.y;
                                if (value >= 1000000) return '$' + Math.round(value / 1000000) + 'M';
                                if (value >= 1000) return '$' + Math.round(value / 1000) + 'K';
                                return '$' + value;
                            },
                            style: {
                                fontWeight: '600',
                                fontSize: '12px',
                                color: '#0B0F15',
                                textOutline: 'none'
                            }
                        }
                    }
                },
                series: [{
                    name: 'Closed Properties',
                    color: '#90B9ED',
                    data: currentAgentData.map(data => ({
                        name: data.CLOSE_OF_ESCROW,
                        y: Number(data.CLOSED_PRICE),
                        closed: Number(data.CLOSED_PROPERTIES)
                    }))
                }],
                legend: { enabled: false },
                credits: { enabled: false }
            };
            return options;
        }

        return null;
    }

    static buildPieChartOptions(inventoryCount, underContractCount) {
        if (inventoryCount === 0 && underContractCount === 0) {
            return null;
        }

        return {
            chart: {
                type: 'pie',
                backgroundColor: '#ffffff',
                height: 300,
            },
            title: { text: '' },
            tooltip: {
                pointFormat: '<b>{point.y}</b> properties ({point.percentage:.1f}%)'
            },
            plotOptions: {
                pie: {
                    size: '65%',
                    borderWidth: 1,
                    borderColor: '#e0e0e0',
                    showInLegend: true,
                    dataLabels: {
                        enabled: true,
                        format: '{point.y}',
                        distance: '-45%',
                        style: {
                            fontWeight: '600',
                            fontSize: '10px',
                            color: '#ffffff',
                            textOutline: 'none'
                        }
                    }
                }
            },
            series: [{
                name: 'Properties',
                data: [
                    { name: 'Current Inventory', y: inventoryCount, color: '#90B9ED' },
                    { name: 'In Negotiation/Under Contract', y: underContractCount, color: '#566C9C' }
                ]
            }],
            legend: {
                enabled: true,
                layout: 'horizontal',
                align: 'center',
                verticalAlign: 'bottom',
                itemStyle: {
                    fontWeight: '400',
                    fontSize: '12px',
                    color: '#4A4A48'
                }
            },
            credits: { enabled: false }
        };
    }

    /**
     * The Summit closings list behind one slice of the Buyer State Breakdown:
     * the same window the chart counts, narrowed to that slice's buyers. Other
     * is every state on file except the ones the chart names beside it.
     */
    static buyerStateListUrl(slice, minDate, namedStates) {
        let buyerState = slice;
        if (slice === 'Unknown') {
            buyerState = 'UNKNOWN';
        } else if (slice === 'Other') {
            buyerState = '!' + namedStates.join(',');
        }
        const query = new URLSearchParams({
            SUMMIT_CLOSINGS: 'true',
            CLOSE_OF_ESCROW_MIN: minDate,
            TRANSACTION_TYPE: INVESTMENT_SALES,
            BUYER_COMPANY_STATE: buyerState,
            order: 'CLOSE_OF_ESCROW.desc',
        });
        return '/list?' + query.toString();
    }

    static buildBuyerStateSlices(items, minDate) {
        if (!items || items.length === 0) {
            return [];
        }

        const stateCounts = {};
        let unknownCount = 0;
        items.forEach(item => {
            const state = item.BUYER_COMPANY_STATE;
            if (state) {
                stateCounts[state] = (stateCounts[state] || 0) + 1;
            } else {
                unknownCount++;
            }
        });

        const sorted = Object.entries(stateCounts).sort((a, b) => b[1] - a[1]);
        const top3 = sorted.slice(0, 3);
        const otherCount = sorted.slice(3).reduce((sum, entry) => sum + entry[1], 0);

        const colors = ['#142345', '#2D5A9E', '#5B8DD9', '#90B9ED'];
        const data = top3.map((entry, i) => ({ name: entry[0], y: entry[1], color: colors[i] }));
        if (otherCount > 0) {
            data.push({ name: 'Other', y: otherCount, color: colors[3] });
        }
        // Closings whose buyer has no state on file. Counted, and kept out of the
        // ranking and off the blues, so the pie totals every closing in the window
        // instead of quietly shrinking to the ones with clean data.
        if (unknownCount > 0) {
            data.push({ name: 'Unknown', y: unknownCount, color: '#6B7280' });
        }
        const namedStates = top3.map(entry => entry[0]);
        data.forEach(point => {
            point.listUrl = Stats.buyerStateListUrl(point.name, minDate, namedStates);
        });

        return data;
    }

    static buildBuyerStatePieOptions(slices) {
        if (slices.length === 0) {
            return null;
        }

        return {
            chart: {
                type: 'pie',
                backgroundColor: '#ffffff',
                // 300 less the room the legend took before it moved out of the
                // chart, so the pie keeps its size beside Portfolio Breakdown.
                height: 225,
            },
            title: { text: '' },
            tooltip: {
                pointFormat: '<b>{point.y}</b> transactions ({point.percentage:.1f}%)'
            },
            plotOptions: {
                pie: {
                    size: '65%',
                    borderWidth: 1,
                    borderColor: '#e0e0e0',
                    dataLabels: {
                        enabled: true,
                        format: '{point.y}',
                        distance: '-45%',
                        style: {
                            fontWeight: '600',
                            fontSize: '10px',
                            color: '#ffffff',
                            textOutline: 'none'
                        }
                    }
                }
            },
            series: [{
                name: 'Buyer State',
                // Highcharts keeps what it is handed, so it gets copies.
                data: slices.map(slice => ({ name: slice.name, y: slice.y, color: slice.color })),
            }],
            // Drawn by renderBuyerStateLegend(): Highcharts sanitises legend
            // HTML and clips labels, which broke the link icons.
            legend: { enabled: false },
            credits: { enabled: false }
        };
    }

    toggleBuyerStateSlice(index, name) {
        const chart = this.buyerStateChart.current && this.buyerStateChart.current.chart;
        const point = chart && chart.series[0] && chart.series[0].points[index];
        if (!point) {
            return;
        }
        const hidden = !this.state.hiddenBuyerStates[name];
        point.setVisible(!hidden);
        this.setState(state => ({ hiddenBuyerStates: { ...state.hiddenBuyerStates, [name]: hidden } }));
    }

    renderBuyerStateLegend() {
        const { buyerStateSlices, hiddenBuyerStates } = this.state;

        return (
            <ul className='stats-dashboard__legend'>
                {buyerStateSlices.map((slice, index) => (
                    <li key={slice.name}
                        className={'stats-dashboard__legend-item' + (hiddenBuyerStates[slice.name] ? ' stats-dashboard__legend-item--hidden' : '')}>
                        <button type='button'
                                className='stats-dashboard__legend-toggle'
                                aria-pressed={!hiddenBuyerStates[slice.name]}
                                onClick={() => this.toggleBuyerStateSlice(index, slice.name)}>
                            <span className='stats-dashboard__legend-dot' style={{ backgroundColor: slice.color }} />
                            {slice.name}
                        </button>
                        <Link className='stats-dashboard__legend-link'
                              to={slice.listUrl}
                              title={`View the ${slice.y} ${slice.name} closings`}
                              aria-label={`View the ${slice.y} ${slice.name} closings`}>
                            <svg viewBox='0 0 24 24' width='10' height='10' aria-hidden='true' fill='none'
                                 stroke='currentColor' strokeWidth='2.75' strokeLinecap='round' strokeLinejoin='round'>
                                <path d='M7 17 17 7' />
                                <path d='M8 7h9v9' />
                            </svg>
                        </Link>
                    </li>
                ))}
            </ul>
        );
    }

    render() {

        let { stats, totalClosedCount, totalClosedSum, isYtdClosing } = this.state;
        const ytd = new Date().getFullYear();
        const d = new Date();
        const date = d.getMonth() + 1 + '/' + d.getDate() + '/' + d.getFullYear();
        return (
            <React.Fragment>
                <div className='Stats'>
                    {this.state.loading && (
                        <div className='Stats_loading'>
                            <div className='Stats_loading-spinner' />
                            <span>Loading...</span>
                        </div>
                    )}
                    {/* <div className='page-rounded-corner-container'>
                        <div className='page-header-container'>
                            <div className='page-header'>
                                <div className='page-title'>Stats</div>
                            </div>
                            <div className='line' />
                        </div>
                    </div> */}
                    <div className='stats-dashboard'>
                        <div className='stats-dashboard__cards'>
                            <div className='stats-dashboard__card'>
                                <InfoColumns title='Current Inventory'
                                    titleRedirect='/list?SUMMIT_INVENTORY=true&order=LISTED_DATE.desc'
                                    description='Properties exclusively listed on the market by Summit'
                                    headerAction={
                                        <Link to='/links' className='stats-title-action'>LINKS</Link>
                                    }
                                    columns={[
                                        { key: 'Properties', value: stats.activeSummitCount },
                                        { key: 'Total Value', value: stats.activeSummitPrice },
                                        { key: 'Ave Cap Rate', value: stats.activeSummitAvgCapRate },
                                        { key: 'Total Sq Ft', value: stats.activeSummitSqft },
                                        { key: 'Ave Time On Market', value: stats.activeSummitAvgTimeOnMarket },
                                    ]}
                                />
                            </div>
                            <div className='stats-dashboard__card'>
                                <InfoColumns title='Closings - YTD'
                                    titleRedirect='/list?SUMMIT_YTD=true&order=CLOSE_OF_ESCROW.desc'
                                    description='Total Summit closings year-to-date'
                                    columns={[
                                        { key: 'Transactions', value: stats.closedYtdCount },
                                        { key: 'Total Value', value: stats.closedYtdPrice },
                                        { key: 'Ave Cap Rate', value: stats.closedYtdAvgCapRate },
                                        { key: 'List:Close Price', value: stats.listClosedPrice },
                                        { key: 'List:Close Time', value: stats.listClosedTime },
                                    ]}
                                />
                            </div>
                            <div className='stats-dashboard__card'>
                                <InfoColumns title='In Negotiation/Under Contract'
                                    titleRedirect='/list?SUMMIT_NEGUC=true&order=STATUS.asc'
                                    description='Current activity & pipeline of Summit properties'
                                    columns={[
                                        { key: 'Properties', value: stats.negUcCount },
                                        { key: 'Total Value', value: stats.negUcPrice },
                                        { key: 'Total Sq Ft', value: stats.negUcSqft },
                                        { key: 'Ave Cap Rate', value: stats.negUcAvgCapRate },
                                    ]}
                                />
                            </div>
                            <div className='stats-dashboard__card'>
                                <InfoColumns title='SUMMIT Closings'
                                    titleRedirect='/list?SUMMIT_CLOSINGS=true&order=CLOSE_OF_ESCROW.desc'
                                    description='Summit transactional history'
                                    columns={[
                                        { key: 'Transactions', value: stats.closedIsCount },
                                        { key: 'Total Value', value: stats.closedIsPrice },
                                        { key: 'Total Sq Ft', value: stats.closedIsSqft },
                                        { key: 'Ave Cap Rate', value: stats.closedIsAvgCapRate },
                                    ]}
                                />
                            </div>
                        </div>

                        <div className='stats-dashboard__charts'>
                            <div className='stats-dashboard__chart-section'>
                                <h2 className='stats-dashboard__section-title'>Transaction History</h2>
                                <p className='stats-dashboard__section-desc'>Annual closed property volume and total value over time</p>
                                <div className='stats-dashboard__chart-body'>
                                    {this.state.barChartData && (
                                        <HighchartsReact
                                            highcharts={Highcharts}
                                            options={this.state.barChartData}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className='stats-dashboard__chart-pies'>
                                <div className='stats-dashboard__chart-pies-container'>
                                    <div className='stats-dashboard__chart-section'>
                                        <h2 className='stats-dashboard__section-title'>Portfolio Breakdown</h2>
                                        <p className='stats-dashboard__section-desc'>Current inventory vs properties under contract or in negotiation</p>
                                        <div className='stats-dashboard__chart-body'>
                                            {this.state.pieChartData && (
                                                <HighchartsReact
                                                    highcharts={Highcharts}
                                                    options={this.state.pieChartData}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className='stats-dashboard__chart-section'>
                                        <h2 className='stats-dashboard__section-title'>Buyer State Breakdown</h2>
                                        <p className='stats-dashboard__section-desc'>Buyer by state (last 18 months)</p>
                                        <div className='stats-dashboard__chart-body'>
                                            {this.state.closingsPieData && (
                                                <React.Fragment>
                                                    <HighchartsReact
                                                        ref={this.buyerStateChart}
                                                        highcharts={Highcharts}
                                                        options={this.state.closingsPieData}
                                                    />
                                                    {this.renderBuyerStateLegend()}
                                                </React.Fragment>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className='stats-dashboard__footer'>
                                    <div className='stats-dashboard__chips'>
                                        <div className='stats-dashboard__chip number_one'>Investment Sale Team<br />in the Southwest</div>
                                        <div className='stats-dashboard__chip'>{totalClosedCount}+ Transactions</div>
                                        <div className='stats-dashboard__chip'>Over ${totalClosedSum} Billion Sold</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='stats-print-container'>
                    <div className='page-height'>
                        <div className='stats-print-container_header'>
                            <h3 className='stats-print-container_h3'>Stats</h3>
                            <h4 className='stats-print-container_h4'>{date}</h4>
                        </div>
                        <div className='marginLeft' style={{ height: '710px' }}>
                            {/* <InfoColumns title={`Closings - ${isYtdClosing ? 'YTD' : ytd}`}
                                titleRedirect={`/list?SOURCE=SUMMIT&STATUS=Closed&CLOSE_OF_ESCROW_MIN=01%2F01%2F${isYtdClosing ? ytd : ytd}&CLOSE_OF_ESCROW_MAX=12%2F31%2F${isYtdClosing ? ytd : ytd}`}
                                columns={[
                                    { key: 'Transactions', value: stats.closedYtdCount },
                                    { key: 'Total Value', value: stats.closedYtdPrice },
                                    { key: 'Ave Cap Rate', value: stats.closedYtdAvgCapRate },
                                    { key: 'List:Close Price', value: stats.listClosedPrice },
                                    { key: 'List:Close Time', value: stats.listClosedTime },
                                ]}
                            /> */}
                            <InfoColumns title={`Closings`}
                                titleRedirect={`/list?SOURCE=SUMMIT&STATUS=Closed`}
                                columns={[
                                    { key: 'Transactions', value: stats.closedTotalCount },
                                    { key: 'Total Value', value: stats.closedTotalPrice },
                                    { key: 'Ave Cap Rate', value: stats.closedTotalAvgCapRate },
                                    { key: 'List:Close Price', value: stats.listClosedTotalPrice },
                                    { key: 'List:Close Time', value: stats.listClosedTotalTime },
                                ]}
                            />
                            {/* <InfoColumns title='Current Inventory'
                                titleRedirect='/list?SOURCE=SUMMIT&STATUS=Active'
                                columns={[
                                    { key: 'Properties', value: stats.activeCount },
                                    { key: 'Total Value', value: stats.activePrice },
                                    { key: 'Ave Cap Rate', value: stats.activeAvgCapRate },
                                    { key: 'Total Sq Ft', value: stats.activeSqft },
                                    { key: 'Ave Time On Market', value: stats.activeAvgTimeOnMarket },
                                ]}
                            /> */}
                            <InfoColumns title='Current Inventory'
                                titleRedirect='/list?SUMMIT_INVENTORY=true&order=LISTED_DATE.desc'
                                columns={[
                                    { key: 'Properties', value: stats.activeVisibleCount },
                                    { key: 'Total Value', value: stats.activeVisiblePrice },
                                    { key: 'Ave Cap Rate', value: stats.activeVisibleAvgCapRate },
                                    { key: 'Total Sq Ft', value: stats.activeVisibleSqft },
                                    { key: 'Ave Time On Market', value: stats.activeVisibleAvgTimeOnMarket },
                                ]}
                            />
                            <InfoColumns title='Total In Negotiation/Under Contract'
                                titleRedirect='/list?SUMMIT_NEGUC=true&order=STATUS.asc'
                                columns={[
                                    { key: 'Properties', value: stats.negUcCount },
                                    { key: 'Total Value', value: stats.negUcPrice },
                                    { key: 'Total Sq Ft', value: stats.negUcSqft },
                                    { key: 'Ave Cap Rate', value: stats.negUcAvgCapRate },
                                ]}
                            />
                            <div className='line' />
                            <InfoColumns title='SUMMIT Closings'
                                titleRedirect='/list?SUMMIT_CLOSINGS=true&order=CLOSE_OF_ESCROW.desc'
                                columns={[
                                    { key: 'Transactions', value: stats.closedIsCount },
                                    { key: 'Total Value', value: stats.closedIsPrice },
                                    { key: 'Total Sq Ft', value: stats.closedIsSqft },
                                    { key: 'Ave Cap Rate', value: stats.closedIsAvgCapRate },
                                ]}
                            />
                        </div>
                        <div className={`print-footer print-footer-3`}>
                            <div className='logo'>
                                <img src={arc.path.media + (summitLogo)} alt='' />
                            </div>
                            (469) 844-8880 | summit.com
                        </div>
                    </div>
                    <div className='page-break' />
                    <div className='page-height'>
                        <div className='high-chart' style={{ height: '758px', marginTop: '50px' }}>

                            <HighchartsReact
                                highcharts={Highcharts}
                                options={this.state.barChartData}
                            />

                        </div>
                        <div className={`print-footer print-footer-3`}>
                            <div className='logo'>
                                <img src={arc.path.media + (summitLogo)} alt='' />
                            </div>
                            (469) 844-8880 | summit.com
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }
}
